import { useEffect, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { gameMapper } from 'utils/casino/maps';
import { CUSTOM_CATEGORIES } from 'utils/constants/casino/custom-categories';
import { NewCasinoGame, NewCasinoGameBackend } from 'interfaces/new-casino';
import { useSelectedCategory } from './useSelectedCategory';
import { getFavoriteCasinoGames } from 'store/selectors/fav-data';
import { getIsLoggedIn } from 'store/selectors/user-data';
import { useCasinoGamesConfigsValues } from '../providers/CasinoGamesConfigsContext';
import type { TCasinoGamesCombinedData } from './useGamesCombinedData';

type UseCombineFavoriteGamesProps = {
  setCombinedData: (
    value:
      | ((prevState: TCasinoGamesCombinedData) => TCasinoGamesCombinedData)
      | TCasinoGamesCombinedData
  ) => void;
};

export const useCombineFavoriteGames = ({
  setCombinedData
}: UseCombineFavoriteGamesProps) => {
  const isLoggedIn = useSelector(getIsLoggedIn);
  const { configs } = useCasinoGamesConfigsValues();
  const favoriteGames: (NewCasinoGame | NewCasinoGameBackend)[] | undefined =
    useSelector(getFavoriteCasinoGames);

  const { selectedCategory } = useSelectedCategory({ configs });

  useLayoutEffect(() => {
    if (selectedCategory !== CUSTOM_CATEGORIES.favorite.id) {
      return;
    }

    if (!isLoggedIn) {
      return;
    }

    setCombinedData(prevData => {
      if (prevData.queryKey !== 'favorite') {
        return {
          queryKey: CUSTOM_CATEGORIES.favorite.id,
          data: undefined
        };
      } else {
        return prevData;
      }
    });
  }, [isLoggedIn]);

  useEffect(() => {
    if (selectedCategory !== CUSTOM_CATEGORIES.favorite.id) {
      return;
    }

    if (!favoriteGames) {
      return;
    }

    setCombinedData({
      queryKey: CUSTOM_CATEGORIES.favorite.id,
      data: {
        data: {
          status: '200',
          games: {
            [CUSTOM_CATEGORIES.favorite.id]: {
              count: favoriteGames.length,
              games: favoriteGames.map(gameMapper)
            }
          }
        }
      }
    });
  }, [selectedCategory, favoriteGames]);
};
